import React from "react";

interface SlideContent {
  quote?: string;
  mainEmoji?: string;
  leftEmoji?: string;
  rightEmoji?: string;
  bgColor?: string;
  onEdit?: (content: SlideContent) => void;
}

const EditableDiv: React.FC<{
  text: string;
  onEdit: (value: string) => void;
  className?: string;
}> = ({ text, onEdit, className = "" }) => {
  return (
    <div
      contentEditable
      suppressContentEditableWarning
      onBlur={(e) => onEdit(e.currentTarget.textContent || "")}
      className={`outline-none hover:ring-2 hover:ring-teal-500/30 rounded px-2 transition-all ${className}`}
    >
      {text}
    </div>
  );
};

const QuoteSlide = {
  id: "quote-slide",
  name: "Quote Slide 1",
  fields: [
    {
      id: "quote",
      label: "Quote Text",
      type: "text",
      defaultValue: "",
    },
    {
      id: "mainEmoji",
      label: "Main Emoji",
      type: "text",
      defaultValue: "🌲",
    },
    {
      id: "leftEmoji",
      label: "Left Emoji",
      type: "text",
      defaultValue: "💫",
    },
    {
      id: "rightEmoji",
      label: "Right Emoji",
      type: "text",
      defaultValue: "✨",
    },
    {
      id: "bgColor",
      label: "Background Color",
      type: "text",
      defaultValue: "linear-gradient(135deg, #0f766e 0%, #134e4a 100%)",
    },
  ],
  render: (content: SlideContent = {}) => {
    const {
      quote = "",
      mainEmoji = "🌲",
      leftEmoji = "💭",
      rightEmoji = "✨",
      bgColor = "linear-gradient(135deg, #0f766e 0%, #134e4a 100%)",
      onEdit = () => {},
    } = content;

    const handleEdit = (field: keyof SlideContent) => (value: string) => {
      const updatedContent = { ...content, [field]: value };
      onEdit(updatedContent);

      if (window.updateLiveConfig) {
        window.updateLiveConfig(updatedContent);
      }
    };

    return (
      <div
        className="w-full h-full relative overflow-hidden text-white"
        style={{ background: bgColor }}
      >
        {/* Background Pattern */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent" />
            <div className="grid grid-cols-10 grid-rows-10">
              {[...Array(100)].map((_, i) => (
                <div
                  key={i}
                  className="w-full h-full border border-white/5"
                  style={{
                    transform: `rotate(${Math.random() * 360}deg)`,
                  }}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Animated Background Circles */}
        <div className="absolute inset-0 overflow-hidden">
          <div
            className="absolute -top-1/2 -left-1/2 w-full h-full rounded-full bg-teal-500/10 animate-pulse"
            style={{ animationDuration: "4s" }}
          />
          <div
            className="absolute -bottom-1/2 -right-1/2 w-full h-full rounded-full bg-teal-500/10 animate-pulse"
            style={{ animationDuration: "6s" }}
          />
        </div>

        {/* Content Container */}
        <div className="relative z-10 flex flex-col items-center justify-center h-full p-12">
          <div
            className="max-w-4xl w-full bg-white/5 backdrop-blur-sm rounded-2xl p-12 border border-white/10 
                        transform hover:scale-[1.02] transition-all duration-500 shadow-2xl"
          >
            {/* Quote Icons */}
            <div className="flex justify-center space-x-8 mb-10 text-6xl">
              <EditableDiv
                text={leftEmoji}
                onEdit={handleEdit("leftEmoji")}
                className="transform hover:scale-110 transition-transform duration-300"
              />
              <EditableDiv
                text={mainEmoji}
                onEdit={handleEdit("mainEmoji")}
                className="transform hover:scale-110 transition-transform duration-300"
              />
              <EditableDiv
                text={rightEmoji}
                onEdit={handleEdit("rightEmoji")}
                className="transform hover:scale-110 transition-transform duration-300"
              />
            </div>

            {/* Quote Text */}
            <div className="relative mb-10">
              {/* Large Quote Marks */}
              <div className="absolute -top-8 -left-8 text-7xl text-teal-300/20 font-serif select-none">
                "
              </div>
              <div className="absolute -bottom-8 -right-8 text-7xl text-teal-300/20 font-serif select-none">
                "
              </div>

              <blockquote className="relative">
                <EditableDiv
                  text={quote}
                  onEdit={handleEdit("quote")}
                  className="text-4xl font-black leading-relaxed text-center text-teal-100 
                           tracking-wide [text-wrap:balance] shadow-lg"
                />
              </blockquote>
            </div>

            {/* Decorative Elements */}
            <div className="flex justify-center items-center gap-4 mt-8">
              <div className="w-24 h-0.5 bg-gradient-to-r from-transparent via-teal-400/30 to-transparent" />
              <div className="w-2 h-2 rounded-full bg-teal-400/30" />
              <div className="w-24 h-0.5 bg-gradient-to-r from-transparent via-teal-400/30 to-transparent" />
            </div>
          </div>
        </div>
      </div>
    );
  },
};

export default QuoteSlide;
